import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { toCamelCaseLabel } from '../utils/camelCaseLabel';
import { webPointer } from '../utils/webLayout';

type Props<T extends string> = {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
};

export function UnderlineTabs<T extends string>({
  options,
  value,
  onChange,
}: Props<T>) {
  const palette = useAppColors();

  return (
    <View
      style={[styles.tabs, { borderBottomColor: palette.border }]}
      accessibilityRole="tablist"
    >
      {options.map(option => {
        const selected = option === value;
        return (
          <Pressable
            key={option}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            accessibilityLabel={toCamelCaseLabel(option)}
            onPress={() => onChange(option)}
            style={[styles.tab, webPointer]}
          >
            <Text
              numberOfLines={1}
              style={[
                styles.tabLabel,
                {
                  color: selected ? palette.primary : palette.textMuted,
                  fontWeight: selected ? '700' : '500',
                },
              ]}
            >
              {toCamelCaseLabel(option)}
            </Text>
            <View
              style={[
                styles.tabUnderline,
                {
                  backgroundColor: selected
                    ? palette.primary
                    : palette.transparent,
                },
              ]}
            />
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  tabs: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: '100%',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tab: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 88,
    alignItems: 'center',
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  tabLabel: {
    ...typography.body,
    fontSize: 15,
    paddingBottom: spacing.sm,
  },
  tabUnderline: {
    alignSelf: 'stretch',
    height: 3,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
});
